interface FingerprintData {
   // HARDWARE-BASED (Very Hard to Change) - Primary components
   hardwareConcurrency: number | null; // CPU cores - requires hardware change
   deviceMemory: number | null; // RAM - requires hardware change
   canvas: string; // Canvas fingerprint - GPU/graphics card dependent
   webgl: string; // WebGL fingerprint - GPU dependent
   webglVendorAndRenderer: string; // GPU vendor/model - hardware
   screenResolution: string; // Monitor resolution - requires monitor change
   availableScreenResolution: string; // Available screen - hardware
   colorDepth: number; // Color depth - monitor/graphics card
   touchSupport: {
      maxTouchPoints: number;
      touchEvent: boolean;
      touchStart: boolean;
   }; // Touch capability - hardware
   audio: string; // Audio fingerprint - hardware-based

   // SYSTEM-BASED (Moderately Stable) - Secondary components
   platform: string; // OS platform - requires OS reinstall
   timezone: string; // Timezone - location-based, relatively stable
   fonts: string[]; // Installed fonts - requires manual changes
   cpuClass: string | null; // CPU architecture - hardware
}

class BrowserFingerprint {
   /**
    * Generate a unique fingerprint ID for the current browser
    * @param hardwareOnly - If true, uses only hardware-based components (most stable)
    */
   async generate(hardwareOnly: boolean = false): Promise<string> {
      const components = await this.getComponents();

      if (hardwareOnly) {
         // Use only hardware-based components for maximum stability
         const hardwareComponents = {
            hardwareConcurrency: components.hardwareConcurrency,
            deviceMemory: components.deviceMemory,
            canvas: components.canvas,
            webgl: components.webgl,
            webglVendorAndRenderer: components.webglVendorAndRenderer,
            screenResolution: components.screenResolution,
            availableScreenResolution: components.availableScreenResolution,
            colorDepth: components.colorDepth,
            touchSupport: components.touchSupport,
            audio: components.audio,
         };
         return this.hashComponents(hardwareComponents as FingerprintData);
      }

      const fingerprint = this.hashComponents(components);
      return fingerprint;
   }

   /**
    * Collect all fingerprint components
    */
   private async getComponents(): Promise<FingerprintData> {
      return {
         // === PRIORITY 1: HARDWARE-BASED (Very Hard to Change) ===
         // These require physical hardware changes and are most reliable
         hardwareConcurrency: navigator.hardwareConcurrency || null, // CPU cores
         deviceMemory: (navigator as any).deviceMemory || null, // RAM amount
         canvas: this.getCanvasFingerprint(), // GPU-based rendering
         webgl: this.getWebGLFingerprint(), // GPU fingerprint
         webglVendorAndRenderer: this.getWebGLVendorAndRenderer(), // GPU vendor/model
         screenResolution: `${window.screen.width}x${window.screen.height}`, // Monitor size
         availableScreenResolution: `${window.screen.availWidth}x${window.screen.availHeight}`, // Screen space
         colorDepth: window.screen.colorDepth, // Monitor color depth
         touchSupport: this.getTouchSupport(), // Touch hardware
         audio: this.getAudioFingerprint(), // Audio hardware

         // === PRIORITY 2: SYSTEM-BASED (Moderately Stable) ===
         // These require system changes but are relatively stable
         platform: navigator.platform, // Operating system
         timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Location/timezone
         fonts: this.getAvailableFonts(), // Installed system fonts
         cpuClass: (navigator as any).cpuClass || null, // CPU architecture
      };
   }

   /**
    * Hash the collected components into a unique fingerprint
    */
   private hashComponents(data: FingerprintData): string {
      const str = JSON.stringify(data);
      return this.hashString(str);
   }

   /**
    * Simple hash function (MurmurHash3)
    */
   private hashString(str: string): string {
      let h1 = 0xdeadbeef;
      let h2 = 0x41c6ce57;

      for (let i = 0, ch; i < str.length; i++) {
         ch = str.charCodeAt(i);
         h1 = Math.imul(h1 ^ ch, 2654435761);
         h2 = Math.imul(h2 ^ ch, 1597334677);
      }

      h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
      h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

      return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
   }

   /**
    * Generate canvas fingerprint
    */
   private getCanvasFingerprint(): string {
      try {
         const canvas = document.createElement('canvas');
         const ctx = canvas.getContext('2d');

         if (!ctx) return '';

         // Draw text with specific styles
         const text = 'BrowserFingerprint@2024 <Canvas> 🎨 1.0';
         ctx.textBaseline = 'top';
         ctx.font = "14px 'Arial'";
         ctx.textBaseline = 'alphabetic';
         ctx.fillStyle = '#f60';
         ctx.fillRect(125, 1, 62, 20);
         ctx.fillStyle = '#069';
         ctx.fillText(text, 2, 15);
         ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
         ctx.fillText(text, 4, 17);

         // Draw some shapes
         ctx.beginPath();
         ctx.arc(50, 50, 30, 0, 2 * Math.PI);
         ctx.stroke();

         return canvas.toDataURL();
      } catch (e) {
         return '';
      }
   }

   /**
    * Generate WebGL fingerprint
    */
   private getWebGLFingerprint(): string {
      try {
         const canvas = document.createElement('canvas');
         const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

         if (!gl) return '';

         // @ts-ignore
         const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
         if (!debugInfo) return '';

         // @ts-ignore
         return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
      } catch (e) {
         return '';
      }
   }

   /**
    * Get WebGL vendor and renderer
    */
   private getWebGLVendorAndRenderer(): string {
      try {
         const canvas = document.createElement('canvas');
         const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

         if (!gl) return '';

         // @ts-ignore
         const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
         if (!debugInfo) return '';

         // @ts-ignore
         const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '';
         // @ts-ignore
         const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';

         return `${vendor}~${renderer}`;
      } catch (e) {
         return '';
      }
   }

   /**
    * Get touch support information
    */
   private getTouchSupport(): { maxTouchPoints: number; touchEvent: boolean; touchStart: boolean } {
      let maxTouchPoints = 0;
      let touchEvent = false;

      if (typeof navigator.maxTouchPoints !== 'undefined') {
         maxTouchPoints = navigator.maxTouchPoints;
      } else if (typeof (navigator as any).msMaxTouchPoints !== 'undefined') {
         maxTouchPoints = (navigator as any).msMaxTouchPoints;
      }

      try {
         document.createEvent('TouchEvent');
         touchEvent = true;
      } catch (e) {
         touchEvent = false;
      }

      const touchStart = 'ontouchstart' in window;

      return {
         maxTouchPoints,
         touchEvent,
         touchStart,
      };
   }

   /**
    * Get available fonts
    */
   private getAvailableFonts(): string[] {
      const baseFonts = ['monospace', 'sans-serif', 'serif'];
      const testString = 'mmmmmmmmmmlli';
      const testSize = '72px';

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return [];

      const detect = (font: string): boolean => {
         const baselineWidths: { [key: string]: number } = {};

         // Get baseline widths
         for (const baseFont of baseFonts) {
            ctx.font = `${testSize} ${baseFont}`;
            baselineWidths[baseFont] = ctx.measureText(testString).width;
         }

         // Compare with test font
         for (const baseFont of baseFonts) {
            ctx.font = `${testSize} '${font}', ${baseFont}`;
            const width = ctx.measureText(testString).width;
            if (width !== baselineWidths[baseFont]) {
               return true;
            }
         }

         return false;
      };

      // Common fonts to test
      const fontList = [
         'Arial',
         'Verdana',
         'Times New Roman',
         'Courier New',
         'Georgia',
         'Palatino',
         'Garamond',
         'Bookman',
         'Comic Sans MS',
         'Trebuchet MS',
         'Impact',
         'Lucida Sans',
         'Tahoma',
         'Helvetica',
         'Monaco',
      ];

      const availableFonts: string[] = [];
      for (const font of fontList) {
         if (detect(font)) {
            availableFonts.push(font);
         }
      }

      return availableFonts.sort();
   }

   /**
    * Generate audio fingerprint
    */
   private getAudioFingerprint(): string {
      try {
         // @ts-ignore
         const AudioContext = window.AudioContext || window.webkitAudioContext;
         if (!AudioContext) return '';

         const context = new AudioContext();
         const oscillator = context.createOscillator();
         const analyser = context.createAnalyser();
         const gainNode = context.createGain();
         const scriptProcessor = context.createScriptProcessor(4096, 1, 1);

         gainNode.gain.value = 0; // Mute
         oscillator.type = 'triangle';
         oscillator.connect(analyser);
         analyser.connect(scriptProcessor);
         scriptProcessor.connect(gainNode);
         gainNode.connect(context.destination);

         oscillator.start(0);

         const fingerprint = analyser.frequencyBinCount.toString();

         oscillator.stop();
         context.close();

         return fingerprint;
      } catch (e) {
         return '';
      }
   }
}

// Export singleton instance
const browserFingerprint = new BrowserFingerprint();

/**
 * Generate a unique browser fingerprint
 * @param hardwareOnly - If true, uses only hardware-based components (most stable, recommended for critical tracking)
 * @returns Promise<string> - Unique fingerprint hash
 *
 * @example
 * // Full fingerprint (all components)
 * const fullFingerprint = await generateFingerprint();
 *
 * // Hardware-only fingerprint (most stable, harder to change)
 * const hardwareFingerprint = await generateFingerprint(true);
 */
export const generateFingerprint = async (hardwareOnly: boolean = false): Promise<string> => {
   return await browserFingerprint.generate(hardwareOnly);
};

export default browserFingerprint;
